import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  TicketCheck,
  Home,
  PackageSearch,
  PlugZap,
  Play,
  Ship,
  Server,
  SearchCode,
  SlidersHorizontal,
} from 'lucide-react';
import { Sidebar, useSidebarPinState } from '@backstage/core-components';
import {
  appThemeApiRef,
  identityApiRef,
  useApi,
  ProfileInfo,
} from '@backstage/core-plugin-api';
import {
  NavContentBlueprint,
  NavContentNavItems,
} from '@backstage/plugin-app-react';
import { useSearchModal } from '@backstage/plugin-search';
import { SearchPaletteContainer } from '../search/SearchPaletteContainer';
import { FeedbackDialog } from '../feedback';
import { QuicklinksLauncher } from '../quicklinks';
import { notificationsApiRef } from '@backstage/plugin-notifications';
import { useSignal } from '@backstage/plugin-signals-react';
import { Icon } from '../../components/ui';
import { useT } from '../i18n';
import { entityRefRoute } from '../catalog/entityMeta';
import { ThemeClassSync } from './ThemeClassSync';
import { SidebarRailView, type RailNavItem } from './SidebarRailView';
import {
  usePendingApprovalsCount,
  useInProgressRunsCount,
} from './useSidebarBadges';
import { useMyCharacters } from '../home/useMyCharacters';
import { characterAvatar } from './characterAvatars';

// Seed the persisted pin state to collapsed on first visit (Backstage defaults
// it to true when the key is missing; our rail starts collapsed).
if (
  typeof window !== 'undefined' &&
  window.localStorage.getItem('sidebarPinState') === null
) {
  window.localStorage.setItem('sidebarPinState', 'false');
}

/**
 * The Regask sidebar rail (connected): wires Backstage state — pin/expand
 * state, active route, theme, identity, search — into the presentational
 * SidebarRailView (built on the design-system primitives, theme-aware).
 */
export const SidebarRail = ({ navItems }: { navItems: NavContentNavItems }) => {
  const t = useT();
  const { isPinned, toggleSidebarPinState, isMobile } = useSidebarPinState();
  // On mobile the rail renders inside the stock overlay menu (a bottom-anchored
  // drawer), so it always shows the full labeled layout and never collapses.
  const expanded = isMobile || isPinned;
  const { pathname } = useLocation();
  const appThemeApi = useApi(appThemeApiRef);
  const identityApi = useApi(identityApiRef);
  const notificationsApi = useApi(notificationsApiRef);
  // The notifications backend publishes signals on the `notifications` channel
  // (new_notification / notification_read / notification_unread). Subscribing
  // lets the badge update in real time instead of waiting for a poll/navigation.
  const { lastSignal } = useSignal('notifications');
  // useSearchModal models visibility as `open && !hidden`: `toggleModal` flips
  // `hidden` (and pins `open` true), and the hook auto-sets `hidden` on route
  // change. So drive the palette from `open && !hidden`, open with the
  // idempotent `setOpen(true)`, and close with `setOpen(false)`.
  const { state: searchState, toggleModal, setOpen } = useSearchModal();
  const searchVisible = searchState.open && !searchState.hidden;
  // The global feedback dialog is mounted here (in the persistent sidebar) the
  // same way the search palette is, so it's reachable from every page.
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [quicklinksOpen, setQuicklinksOpen] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // Cmd/Ctrl+K opens search; the Shift variant is quicklinks (handled in
      // QuicklinksLauncher), so exclude it here to avoid opening both.
      if (
        (e.metaKey || e.ctrlKey) &&
        !e.shiftKey &&
        e.key.toLowerCase() === 'k'
      ) {
        e.preventDefault();
        setOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [setOpen]);

  const [themeId, setThemeId] = useState(appThemeApi.getActiveThemeId());
  const [profile, setProfile] = useState<ProfileInfo>();
  const [profileTarget, setProfileTarget] = useState('/settings');
  const [unreadCount, setUnreadCount] = useState(0);
  const [isAdmin, setIsAdmin] = useState(false);
  // Swap the avatar to a character crest when the signed-in user holds a
  // character. With multiple characters the first one with a crest wins (the
  // greeting-header badges show them all).
  const { characters: myCharacters } = useMyCharacters();
  const characterAvatarUri = myCharacters
    .map(c => characterAvatar(c.id))
    .find(Boolean);

  useEffect(() => {
    const sub = appThemeApi.activeThemeId$().subscribe(id => setThemeId(id));
    return () => sub.unsubscribe();
  }, [appThemeApi]);

  useEffect(() => {
    let cancelled = false;
    identityApi
      .getProfileInfo()
      .then(p => !cancelled && setProfile(p))
      .catch(() => {});
    identityApi
      .getBackstageIdentity()
      .then(identity => {
        if (cancelled) return;
        const route = entityRefRoute(identity.userEntityRef);
        if (route) setProfileTarget(route);
        setIsAdmin(
          (identity.ownershipEntityRefs ?? []).includes(
            'group:default/backstage-admin',
          ),
        );
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [identityApi]);

  const refreshUnread = useCallback(() => {
    notificationsApi
      .getStatus()
      .then(status => setUnreadCount(status.unread))
      .catch(() => {});
  }, [notificationsApi]);

  // Keep the unread badge fresh: fetch on mount, on every navigation (so it
  // updates after the user reads notifications), on tab focus, and a light poll
  // as a fallback when signals aren't available.
  useEffect(() => {
    refreshUnread();
    window.addEventListener('focus', refreshUnread);
    const timer = window.setInterval(refreshUnread, 60_000);
    return () => {
      window.removeEventListener('focus', refreshUnread);
      window.clearInterval(timer);
    };
  }, [refreshUnread, pathname]);

  // Real-time: refetch the count whenever the backend signals a notification
  // change, so the badge updates without a navigation or poll cycle.
  useEffect(() => {
    if (lastSignal) refreshUnread();
  }, [lastSignal, refreshUnread]);

  // Pending approvals (real-time via the `approvals` signal) and in-progress
  // runs (poll fallback) drive their nav-item badges.
  const pendingApprovals = usePendingApprovalsCount();
  const runsInProgress = useInProgressRunsCount();

  const isActive = (to: string, exact = false) =>
    exact ? pathname === to : pathname === to || pathname.startsWith(`${to}/`);

  const href = (id: string, fallback: string) =>
    navItems.take(id)?.href ?? fallback;

  const items: RailNavItem[] = [
    {
      key: 'home',
      to: href('page:home', '/'),
      label: t('nav.sidebar.homeItem'),
      icon: <Icon as={Home} size="md" />,
      active: isActive(href('page:home', '/'), true),
      accent: 'indigo',
    },
    {
      key: 'catalog',
      to: href('page:catalog', '/catalog'),
      label: t('nav.sidebar.catalogItem'),
      icon: <Icon as={PackageSearch} size="md" />,
      active: isActive(href('page:catalog', '/catalog')),
      accent: 'violet',
    },
    {
      key: 'deployments',
      to: '/deployments',
      label: t('nav.sidebar.deploymentsItem'),
      icon: <Icon as={Ship} size="md" />,
      active: isActive('/deployments'),
      accent: 'emerald',
    },
    {
      key: 'k8s',
      to: href('page:k8s-overview', '/kubernetes'),
      label: t('nav.sidebar.kubernetesItem'),
      icon: <Icon as={Server} size="md" />,
      active: isActive(href('page:k8s-overview', '/kubernetes')),
      accent: 'sky',
    },
    {
      key: 'external-tools',
      to: '/external-tools',
      label: t('nav.sidebar.externalToolsItem'),
      icon: <Icon as={PlugZap} size="md" />,
      active: isActive('/external-tools'),
      accent: 'cyan',
    },
    {
      key: 'query',
      to: '/query',
      label: t('nav.sidebar.queryItem'),
      icon: <Icon as={SearchCode} size="md" />,
      active: isActive('/query'),
      accent: 'fuchsia',
    },
    {
      key: 'approvals',
      to: '/approvals',
      label: t('nav.sidebar.approvalsItem'),
      icon: <Icon as={TicketCheck} size="md" />,
      active: isActive('/approvals'),
      accent: 'amber',
      badge: pendingApprovals,
    },
    {
      key: 'actions',
      to: '/actions',
      label: t('nav.sidebar.actionsItem'),
      icon: <Icon as={Play} size="md" />,
      active: isActive('/actions'),
      accent: 'rose',
      badge: runsInProgress,
    },
  ];

  if (isAdmin) {
    items.push({
      key: 'admin',
      to: '/admin',
      label: t('nav.sidebar.adminItem'),
      icon: <Icon as={SlidersHorizontal} size="md" />,
      active: isActive('/admin'),
      accent: 'teal',
    });
  }

  const isDark = themeId !== 'light';

  return (
    <>
      <SidebarRailView
        expanded={expanded}
        onToggleExpand={toggleSidebarPinState}
        hideExpandToggle={isMobile}
        navItems={items}
        isDark={isDark}
        onToggleTheme={() =>
          appThemeApi.setActiveThemeId(isDark ? 'light' : 'dark')
        }
        onSearch={toggleModal}
        onQuicklinks={() => setQuicklinksOpen(true)}
        onFeedback={() => setFeedbackOpen(true)}
        notificationsActive={isActive('/notifications')}
        notificationsCount={unreadCount}
        settingsActive={isActive('/settings')}
        helpActive={isActive('/help')}
        profile={{
          name: profile?.displayName ?? t('nav.sidebar.profileFallbackName'),
          email: profile?.email,
          picture: characterAvatarUri ?? profile?.picture,
          to: profileTarget,
        }}
      />
      <SearchPaletteContainer
        open={searchVisible}
        onClose={() => setOpen(false)}
      />
      <FeedbackDialog
        open={feedbackOpen}
        onClose={() => setFeedbackOpen(false)}
      />
      <QuicklinksLauncher
        managerOpen={quicklinksOpen}
        onOpenManager={() => setQuicklinksOpen(true)}
        onCloseManager={() => setQuicklinksOpen(false)}
      />
    </>
  );
};

export const SidebarContent = NavContentBlueprint.make({
  params: {
    component: ({ navItems }) => (
      <Sidebar disableExpandOnHover>
        <ThemeClassSync />
        <SidebarRail navItems={navItems} />
      </Sidebar>
    ),
  },
});
